<template>
  <v-combobox
    v-model="model"
    :items="suggestionItems"
    :search-input.sync="search"
    :rules="rulesList"
    :label="label"
    :disabled="disabled"
    :dense="dense"
    :hide-details="hideDetails"
    :no-filter="true"
    multiple
    clearable
    hide-selected
    small-chips
    flat
    outlined
    @update:search-input="onSearchInput"
    @focus="onFocus"
    @blur="onBlur"
  >
    <template v-slot:append>
      <slot name="appendActions" />
      <person-lookup-menu :partnerId="partnerId" @select="applyPerson" />
    </template>

    <template v-slot:item="{ item }">
      <v-list-item-avatar>
        <v-avatar color="primary" class="white--text">
          {{ avatar(item) }}
        </v-avatar>
      </v-list-item-avatar>
      <v-list-item-content>
        <v-list-item-title>{{ personLabelFor(item) || item }}</v-list-item-title>
        <v-list-item-subtitle v-if="personLabelFor(item)">{{ item }}</v-list-item-subtitle>
      </v-list-item-content>
    </template>

    <template v-slot:selection="{ attrs, item, parent, selected }">
      <v-chip
        v-bind="attrs"
        :input-value="selected"
        :color="disabled ? '#F3F3F3' : 'light-grey'"
        :x-small="dense"
        label
        small
        outlined
      >
        <span class="pr-2">{{ item }}</span>
        <v-icon small @click="removeItem(item, parent)" :color="disabled ? 'grey' : 'light-grey'">$delete</v-icon>
      </v-chip>
    </template>
  </v-combobox>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { debounce } from "debounce";
import { requiredRule } from "@/lib/rules/requiredRule";
import { PersonSearchModule } from "@/store/modules/search.store";
import { MrfiktivPersonControllerGetParamsGen } from "@/services/mrfiktiv/v1/data-contracts";
import { avatar } from "@/lib/utility/mail-helper";
import PersonLookupMenu, { IPersonLookupSelection } from "@/components/person/PersonLookupMenu.vue";

@Component({ components: { PersonLookupMenu } })
export default class RecipientSelect extends Vue {
  @Prop({ default: () => [] })
  items!: string[];

  @Prop()
  prefill?: string[];

  @Prop()
  rules?: Function[];

  @Prop({ default: () => [] })
  recommendedReceivers!: string[];

  @Prop()
  partnerId?: string;

  @Prop()
  label?: string;

  @Prop({ default: false })
  disabled?: boolean;

  @Prop()
  dense?: boolean;

  @Prop()
  hideDetails?: boolean | string;

  @Prop({ default: true })
  isRequired?: boolean;

  model: string[] = this.prefill !== undefined ? [...this.prefill] : [...(this.items ?? [])];

  search: string | null = null;
  isFocused = false;

  /** Backend email suggestions for the current query (local copy, not read reactively from the store). */
  emailSuggestions: string[] = [];
  /** email -> "First Last" label, for rendering suggestions as name + email. */
  personLabelByEmail: Record<string, string> = {};

  debounceEmailSearch = debounce((val: string) => {
    this.searchEmails(val);
  }, 300);

  get rulesList() {
    return [this.forEachRules];
  }

  /** When the field is empty/focused show recommendations; while typing show backend suggestions. */
  get suggestionItems(): string[] {
    if (this.search && this.search.length >= 2) {
      return this.emailSuggestions;
    }
    if (this.isFocused) {
      return (this.recommendedReceivers ?? []).filter((email) => !this.model.includes(email));
    }
    return [];
  }

  mounted() {
    this.emitSelection();
  }

  personLabelFor(email: string): string {
    return this.personLabelByEmail[email] ?? "";
  }

  /** Initials avatar for a receiver email, matching the message-list display. */
  avatar(email: string): string {
    return avatar(email);
  }

  onFocus() {
    this.isFocused = true;
  }

  onSearchInput(val: string | null) {
    if (!val || val.length < 2) {
      this.emailSuggestions = [];
      return;
    }
    this.debounceEmailSearch(val);
  }

  async searchEmails(pattern: string) {
    const partnerId = this.partnerId;
    if (!pattern || pattern.length < 2 || !partnerId) {
      this.emailSuggestions = [];
      return;
    }
    try {
      PersonSearchModule.setSearch(pattern);
      const query: MrfiktivPersonControllerGetParamsGen = { partnerId };
      const persons = (await PersonSearchModule.fetchPage(query)) ?? [];
      const labelByEmail: Record<string, string> = {};
      const emails: string[] = [];
      for (const person of persons) {
        const name = `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim();
        for (const e of person.emails ?? []) {
          if (!e.email || emails.includes(e.email)) continue;
          emails.push(e.email);
          if (name) labelByEmail[e.email] = name;
        }
      }
      this.personLabelByEmail = labelByEmail;
      this.emailSuggestions = emails;
    } catch (error) {
      this.$log.error(error);
    }
  }

  applyPerson({ email }: IPersonLookupSelection) {
    if (!this.model.includes(email)) {
      this.model = [...this.model, email];
    }
  }

  removeItem(item: string, parent: any) {
    parent.selectItem(item);
  }

  forEachRules(values: string[]) {
    const resultMessages: string[] = [];
    for (const rule of this.rules || []) {
      let message = "";
      (values || []).forEach((value) => {
        const res = rule(value);
        if (res !== true) {
          message = res as string;
        }
      });
      if (message) resultMessages.push(message);
    }
    if (this.isRequired && this.model?.length === 0) {
      resultMessages.push(requiredRule()("") as string);
    }
    return resultMessages.length > 0 ? resultMessages.join(", ") : true;
  }

  get isValid(): boolean {
    return this.forEachRules(this.model) === true;
  }

  @Watch("model")
  emitSelection() {
    this.$emit("changeSelection", this.model);
    this.$emit("changeIsValid", this.isValid);
  }

  onBlur() {
    this.isFocused = false;
    this.$emit("blur");
  }
}
</script>
